import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, updateDoc, doc } from "firebase/firestore";
import { formatCurrency } from "@/config/company.config";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Users, Search, Plus, Mail, Phone, Building, Calendar, UserPlus, DollarSign, Briefcase } from "lucide-react";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  type: 'buyer' | 'seller' | 'broker';
  addedAt: any;
  activeMandates: number;
  totalPaid: number;
  status: 'active' | 'inactive' | 'completed';
}

const mockClients: Client[] = [
  {
    id: "mock-client-1",
    name: "Jean-Pierre Martin",
    email: "jp.martin@example.com",
    phone: "+1 514-555-1234",
    company: "Martin Industries Inc.",
    type: "buyer",
    addedAt: new Date(),
    activeMandates: 2,
    totalPaid: 15000,
    status: "active",
  },
  {
    id: "mock-client-2",
    name: "Sophie Tremblay",
    email: "s.tremblay@example.com",
    phone: "+1 438-555-5678",
    company: "Tremblay & Associés",
    type: "seller",
    addedAt: new Date(),
    activeMandates: 1,
    totalPaid: 8500,
    status: "active",
  },
  {
    id: "mock-client-3",
    name: "Marc Dubois",
    email: "m.dubois@broker.com",
    phone: "+1 613-555-9012",
    company: "Dubois Brokerage",
    type: "broker",
    addedAt: new Date(),
    activeMandates: 3,
    totalPaid: 22000,
    status: "active",
  },
  {
    id: "mock-client-4",
    name: "Claire Beaumont",
    email: "c.beaumont@example.com",
    phone: "+1 416-555-3456",
    company: "Beaumont Holdings",
    type: "buyer",
    addedAt: new Date(),
    activeMandates: 0,
    totalPaid: 5000,
    status: "inactive",
  },
];

const translations = {
  en: {
    title: "My Clients",
    subtitle: "Buyers, sellers, and brokers you work with",
    addClient: "Add Client",
    searchClients: "Search clients...",
    allStatus: "All Status",
    active: "Active",
    inactive: "Inactive",
    completed: "Completed",
    noClients: "No clients yet",
    addFirstClient: "Add Your First Client",
    totalPaid: "Total Paid",
    activeMandates: "Active Mandates",
    contact: "Contact",
    viewProfile: "View Profile",
    mergeCalendar: "Merge Calendar",
    buyer: "Buyer",
    seller: "Seller",
    broker: "Broker",
    newClient: "New Client",
    clientName: "Client Name",
    email: "Email",
    phone: "Phone",
    company: "Company",
    clientType: "Client Type",
    cancel: "Cancel",
    save: "Save Client",
    loading: "Loading clients...",
    clientAdded: "Client Added",
    clientAddedDesc: "New client has been added successfully",
    error: "Error",
    errorDesc: "Failed to add client",
  },
  fr: {
    title: "Mes Clients",
    subtitle: "Acheteurs, vendeurs et courtiers avec qui vous travaillez",
    addClient: "Ajouter Client",
    searchClients: "Rechercher clients...",
    allStatus: "Tous Statuts",
    active: "Actif",
    inactive: "Inactif",
    completed: "Complété",
    noClients: "Aucun client pour le moment",
    addFirstClient: "Ajoutez Votre Premier Client",
    totalPaid: "Total Payé",
    activeMandates: "Mandats Actifs",
    contact: "Contact",
    viewProfile: "Voir Profil",
    mergeCalendar: "Fusionner Calendrier",
    buyer: "Acheteur",
    seller: "Vendeur",
    broker: "Courtier",
    newClient: "Nouveau Client",
    clientName: "Nom du Client",
    email: "Email",
    phone: "Téléphone",
    company: "Entreprise",
    clientType: "Type de Client",
    cancel: "Annuler",
    save: "Enregistrer Client",
    loading: "Chargement des clients...",
    clientAdded: "Client Ajouté",
    clientAddedDesc: "Le nouveau client a été ajouté avec succès",
    error: "Erreur",
    errorDesc: "Échec de l'ajout du client",
  },
};

export default function ConsultantClients() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    type: "buyer" as 'buyer' | 'seller' | 'broker',
  });

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  const loadClients = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const clientsRef = collection(db, "consultantClients");
      const clientsQuery = query(
        clientsRef,
        where("consultantId", "==", user.email),
        orderBy("addedAt", "desc")
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
      setClients(clientsData.length > 0 ? clientsData : mockClients);
    } catch (error) {
      console.error("Error loading clients:", error);
      setClients(mockClients);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!user || !newClient.name || !newClient.email) return;
    
    try {
      await addDoc(collection(db, "consultantClients"), {
        ...newClient,
        consultantId: user.email,
        addedAt: serverTimestamp(),
        activeMandates: 0,
        totalPaid: 0,
        status: "active",
      });
      
      toast({
        title: t.clientAdded,
        description: t.clientAddedDesc,
      });
      
      setIsDialogOpen(false);
      setNewClient({ name: "", email: "", phone: "", company: "", type: "buyer" });
      loadClients();
    } catch (error) {
      console.error("Error adding client:", error);
      toast({
        title: t.error,
        description: t.errorDesc,
        variant: "destructive",
      });
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || client.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTypeLabel = (type: string) => {
    const labels = { buyer: t.buyer, seller: t.seller, broker: t.broker };
    return labels[type as keyof typeof labels] || type;
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'buyer': return 'default';
      case 'seller': return 'secondary';
      case 'broker': return 'outline';
      default: return 'outline';
    }
  };

  // formatCurrency is now imported from company.config

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              {t.addClient}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.newClient}</DialogTitle>
              <DialogDescription>
                {language === 'fr' ? 'Ajoutez un nouveau client à votre liste' : 'Add a new client to your list'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t.clientName}</Label>
                <Input
                  id="name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone">{t.phone}</Label>
                <Input
                  id="phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="+1 555-123-4567"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="company">{t.company}</Label>
                <Input
                  id="company"
                  value={newClient.company}
                  onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                  placeholder="Company Inc."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="type">{t.clientType}</Label>
                <Select
                  value={newClient.type}
                  onValueChange={(value: 'buyer' | 'seller' | 'broker') => setNewClient({ ...newClient, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">{t.buyer}</SelectItem>
                    <SelectItem value="seller">{t.seller}</SelectItem>
                    <SelectItem value="broker">{t.broker}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>{t.cancel}</Button>
              <Button onClick={handleAddClient}>{t.save}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t.searchClients}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder={t.allStatus} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allStatus}</SelectItem>
            <SelectItem value="active">{t.active}</SelectItem>
            <SelectItem value="inactive">{t.inactive}</SelectItem>
            <SelectItem value="completed">{t.completed}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clients Grid */}
      {filteredClients.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredClients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {client.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{client.name}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <Building className="h-3 w-3" />
                      <span className="truncate">{client.company || 'Independent'}</span>
                    </CardDescription>
                  </div>
                  <Badge variant={getTypeBadgeVariant(client.type)}>
                    {getTypeLabel(client.type)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{client.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{client.phone}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <Briefcase className="h-4 w-4" />
                    </div>
                    <p className="text-lg font-semibold">{client.activeMandates}</p>
                    <p className="text-xs text-muted-foreground">{t.activeMandates}</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <p className="text-lg font-semibold">{formatCurrency(client.totalPaid)}</p>
                    <p className="text-xs text-muted-foreground">{t.totalPaid}</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    <Mail className="h-4 w-4 mr-1" />
                    {t.contact}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <Calendar className="h-4 w-4 mr-1" />
                    {t.mergeCalendar}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t.noClients}</h3>
            <p className="text-muted-foreground text-center mt-2 mb-4">
              {language === 'fr' 
                ? 'Commencez à ajouter des clients pour gérer vos mandats'
                : 'Start adding clients to manage your mandates'}
            </p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t.addFirstClient}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
